import { EXPORT_FORMATS } from '../constants';
import type { EditorState, ExportFormat } from '../types';
import { ensureFonts } from './fonts';
import { renderThumbnail, type RenderSource } from './render';

export interface ExportOptions extends RenderSource {
  state: EditorState;
  width: number;
  height: number;
  format: ExportFormat;
  /** 1–100, ignored for PNG. */
  quality: number;
}

function mimeFor(format: ExportFormat): string {
  return EXPORT_FORMATS.find((f) => f.value === format)?.mime ?? 'image/png';
}

export function extensionFor(format: ExportFormat): string {
  return EXPORT_FORMATS.find((f) => f.value === format)?.ext ?? 'png';
}

function createCanvas(width: number, height: number): {
  canvas: OffscreenCanvas | HTMLCanvasElement;
  ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;
} {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d');
    if (ctx) return { canvas, ctx };
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context is unavailable in this browser.');
  return { canvas, ctx };
}

function toBlob(
  canvas: OffscreenCanvas | HTMLCanvasElement,
  type: string,
  quality: number | undefined,
): Promise<Blob> {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob(quality === undefined ? { type } : { type, quality });
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas export failed.'))),
      type,
      quality,
    );
  });
}

/** Renders at the exact requested pixel size — no DOM capture, no upscaling. */
export async function exportThumbnail(options: ExportOptions): Promise<Blob> {
  const { state, width, height, format, quality } = options;

  await ensureFonts([
    { family: state.main.fontFamily, weight: state.main.fontWeight },
    { family: state.sub.fontFamily, weight: state.sub.fontWeight },
  ]);

  const { canvas, ctx } = createCanvas(width, height);
  renderThumbnail(ctx, {
    state,
    image: options.image,
    imageWidth: options.imageWidth,
    imageHeight: options.imageHeight,
    width,
    height,
    selection: null,
  });

  const isLossy = format !== 'png';
  return toBlob(canvas, mimeFor(format), isLossy ? quality / 100 : undefined);
}

/**
 * Names the file after the MIME type the browser actually produced. A browser
 * that does not support the requested type silently falls back to PNG, and a
 * PNG called `.webp` confuses everything downstream.
 */
export function buildFileName(state: EditorState, mimeType: string): string {
  const slug = state.main.text
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\p{L}\p{N}-]/gu, '')
    .slice(0, 40);
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const base = slug.length > 0 ? `${slug}-${stamp}` : `thumbnail-${stamp}`;
  const extension = EXPORT_FORMATS.find((f) => f.mime === mimeType)?.ext ?? 'png';
  return `${base}.${extension}`;
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export async function copyBlobToClipboard(blob: Blob): Promise<void> {
  if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
    throw new Error('Clipboard images are not supported in this browser.');
  }
  await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
}
